import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { NumberParam, useQueryParams } from 'use-query-params'

import translationKeys from '@translationKeys'

import BlankStateImage from '@assets/images/blank_state_placeholder.jpg'
import alertsApi from '@modules/alerts/api'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { ProfileType } from '@modules/search/types'
import Typography from '@mui/material/Typography'
import { useSavedScrollPosition } from '@shared/hooks/useSavedScrollPosition'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import updatesApi from '../../api'
import { PagePaper, PagePaperInner, PageWrapper, SubtitleContainer, SubtitleInner, TitleContainer } from '../../index.styles'
import CheckmarkInCircleIcon from './components/CheckmarkInCircleIcon'
import UpdatesTable from './components/UpdatesTable'
import {
  EmptyUpdatesButton,
  EmptyUpdatesInner,
  EmptyUpdatesList,
  EmptyUpdatesListItem,
  EmptyUpdatesPicture,
  EmptyUpdatesWrapper,
} from './index.styles'
import { Update, UpdatesPageDataDto, UpdatesPageProps } from './types'

const UpdatesPage = ({ alertsAreSet }: UpdatesPageProps) => {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [tablePage, setTablePage] = useState(1)

  const [query, setQuery] = useQueryParams({
    page: NumberParam,
  })

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [updatesApi.getUpdates.key, user?.id],
    queryFn: ({ pageParam }) => {
      return updatesApi.getUpdates.fn({ limit: 50, lastEvaluatedKey: pageParam || undefined })
    },
    enabled: !!user?.id,
    initialPageParam: '',
    getNextPageParam: lastPage => {
      if (!lastPage.updates?.items?.length || !lastPage.updates?.lastEvaluatedKey) {
        return undefined
      }

      return lastPage.updates.lastEvaluatedKey || undefined
    },
  })

  const { saveScroll } = useSavedScrollPosition({ dependencies: [isLoading], condition: !isLoading })

  useEffect(() => {
    if (!isLoading && query.page) {
      setTablePage(query.page)
      setQuery({ page: undefined })
    }
  }, [isLoading])

  const { mutate: updateProfileAlertViewed } = useMutation({
    mutationFn: alertsApi.updateProfileAlertViewed.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [updatesApi.getUpdates.key] })
      queryClient.invalidateQueries({ queryKey: [updatesApi.getUpdatesUnreadCount.key] })
    },
  })

  const { mutate: updateProfileAlertsAllViewed } = useMutation({
    mutationFn: alertsApi.updateProfileAlertsAllViewed.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [updatesApi.getUpdates.key] })
      queryClient.invalidateQueries({ queryKey: [updatesApi.getUpdatesUnreadCount.key] })
    },
  })

  const updatesPageData: Update[] = data?.pages.map((p: UpdatesPageDataDto) => p.updates?.items).flat() || []

  const onMarkAllAsRead = () => {
    updateProfileAlertsAllViewed()
  }

  const onMarkAsRead = (profileId: string, profileType: ProfileType) => {
    updateProfileAlertViewed([{ id: profileId, '@type': profileType }])
  }

  const handleClickProfile = () => {
    saveScroll()
    setQuery({ page: tablePage }, 'pushIn')
  }

  return (
    <PageWrapper>
      {!isLoading && (
        <PagePaper>
          <PagePaperInner>
            <TitleContainer>
              <Typography variant="h2">{t(translationKeys.UPDATES)}</Typography>
            </TitleContainer>

            {updatesPageData?.length ? (
              <SubtitleContainer>
                <SubtitleInner onClick={onMarkAllAsRead}>
                  {t(translationKeys.MARK_ALL_AS_READ_WITHOUT_CHECKMARK)}
                  <CheckmarkInCircleIcon isHighlighted isSmall />
                </SubtitleInner>
              </SubtitleContainer>
            ) : null}

            {updatesPageData?.length ? (
              <UpdatesTable
                hasNextPage={hasNextPage}
                updatesPageData={updatesPageData}
                isPageDataLoading={false}
                onMarkAsRead={onMarkAsRead}
                onClickProfile={handleClickProfile}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            ) : null}

            {!updatesPageData?.length && (
              <EmptyUpdatesWrapper>
                <EmptyUpdatesInner>
                  {alertsAreSet ? (
                    <>
                      <Typography variant="h2" textAlign="left" alignSelf="flex-start" marginBottom="8px">
                        {t(translationKeys.GOOD_THINGS_COME)}
                      </Typography>
                      <Typography variant="h3" textAlign="left" marginBottom="16px">
                        {t(translationKeys.THERE_ARE_NO_UPDATES)}
                      </Typography>
                      <Typography variant="h3" textAlign="left" marginBottom="12px">
                        {t(translationKeys.IN_THE_MEANTIME)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h2" textAlign="left" alignSelf="flex-start" marginBottom="8px">
                        {t(translationKeys.YOU_ARE_MISSING_OUT)}
                      </Typography>
                      <Typography variant="h3" textAlign="left" marginBottom="16px">
                        {t(translationKeys.YOU_HAVE_NO_ALERTS_SET_UP)}
                      </Typography>
                      <Typography variant="h3" textAlign="left" alignSelf="flex-start">
                        {t(translationKeys.FOR_EXAMPLE)}
                      </Typography>
                      <EmptyUpdatesList>
                        <EmptyUpdatesListItem>
                          <Typography variant="h3" textAlign="left">
                            {t(translationKeys.TRACK_WHEN_ASSETS_CHANGE)}
                          </Typography>
                        </EmptyUpdatesListItem>
                        <EmptyUpdatesListItem>
                          <Typography variant="h3" textAlign="left">
                            {t(translationKeys.FIND_OUT_WHICH_EVENT)}
                          </Typography>
                        </EmptyUpdatesListItem>
                      </EmptyUpdatesList>
                    </>
                  )}

                  <EmptyUpdatesButton onClick={() => navigate('/plus/search')}>
                    {t(translationKeys.GO_TO_SEARCH_TO_GET_STARTED)}
                  </EmptyUpdatesButton>
                  <EmptyUpdatesPicture src={BlankStateImage} alt="blank state image" />
                </EmptyUpdatesInner>
              </EmptyUpdatesWrapper>
            )}
          </PagePaperInner>
        </PagePaper>
      )}
    </PageWrapper>
  )
}
export default UpdatesPage
